import React from 'react';
import moment from 'moment';
import {useSelector} from 'react-redux';
import { Holiday, DailyDateInfo } from '../types';
import {
  createDateInfo,
} from '../utils/dateUtils';
import {
  CurrentDateFlag,
  DateIndicatorFlag,
  DayHeader,
  GridLine,
  HeaderSection,
  HolidayFlag,
  Milestone,
  MilestoneFlag,
  StartDateFlag,
  EndDateFlag
} from './GridComponents';
import {getOrgWorkingDays, isOrgWeekend} from '../../../utils/helperUtils';
import {useTimelineMouseTracking} from '../hooks/useTimelineMouseTracking';
import {ENTITY} from "../../../redux/slices/timeline";

interface QuarterlyGridViewProps {
  startDate: string;
  endDate: string;
  spacing: number;
  totalHeight: number;
  orgId: string;
  milestones?: Milestone[];
}

const QuarterlyGridView: React.FC<QuarterlyGridViewProps> = ({
  startDate,
  endDate,
  spacing,
  totalHeight,
  orgId,
  milestones
}) => {
  const {
    orgSchedule: organizationHolidays = [],
  } = useSelector((state: any) => state.organization);

  const {
    currentEntity,
  } = useSelector((state: any) => state.timeline);

  const organizationWorkingDays = getOrgWorkingDays(orgId || '');
  const holidayDates = new Set(
    organizationHolidays.map((h: Holiday) => (h?.date || '').split('T')[0])
  );
  const isWorkingDay = (dateStr: string) => {
    const weekend = isOrgWeekend(dateStr, organizationWorkingDays);
    const isHoliday = holidayDates.has(dateStr);
    return !weekend && !isHoliday;
  };
  const getNextWorkingDay = (d: string) => {
    let cur = moment(d).add(1, 'day');
    while (true) {
      const ds = cur.format('YYYY-MM-DD');
      if (isWorkingDay(ds)) return ds;
      cur = cur.add(1, 'day');
    }
  };

  const generateQuarterDates = (date: moment.Moment) => {
    const dates: (DailyDateInfo & { dayNumber: number; monthYear: string })[] = [];

    const quarterStart = date.clone().startOf('month');
    const quarterEnd = date.clone().endOf('month');

    const current = quarterStart.clone();
    while (current.isSameOrBefore(quarterEnd)) {
      const currentDateStr = current.format('YYYY-MM-DD');
      const isWeekend = isOrgWeekend(currentDateStr, organizationWorkingDays);
      const isHoliday = organizationHolidays.some((h: Holiday) => h.date.split('T')[0] === currentDateStr);

      if (!isWeekend && !isHoliday) {
        const dateInfo = createDateInfo(currentDateStr, organizationHolidays, organizationWorkingDays, 'quarterly');
        dates.push({
          ...dateInfo,
          dayNumber: current.date(),
          monthYear: current.format('MMMM YYYY'),
        });
      }
      current.add(1, 'day');
    }

    return dates;
  };

  const processQuarters = () => {
    const allDates: ReturnType<typeof generateQuarterDates> = [];

    // Start from the project's start month (not the quarter)
    const periodStart = moment(startDate).startOf('month');
    const endQuarter = moment(endDate).endOf('quarter');

    // Preserve existing minimum span logic: if end is within the same quarter as start,
    // extend to next quarter's end to ensure a reasonable width
    const startQuarterForSpan = periodStart.clone().startOf('quarter');
    const quartersDiff = moment(endQuarter).diff(startQuarterForSpan, 'quarters');
    const effectiveEnd = quartersDiff < 1
      ? startQuarterForSpan.clone().add(1, 'quarter').endOf('quarter')
      : endQuarter;

    // Generate dates from periodStart to effectiveEnd month-by-month
    let cursor = periodStart.clone();
    while (cursor.isSameOrBefore(effectiveEnd)) {
      allDates.push(...generateQuarterDates(cursor));
      cursor.add(1, 'month');
    }

    return allDates;
  };

  const allDates = processQuarters();

  const { isMouseInTimeline, mousePosition, topPosition, mouseHandlers } = useTimelineMouseTracking({
    dates: allDates,
    spacing,
    viewType: 'quarterly'
  });

  const quarters = allDates.reduce((acc: { [key: string]: number }, date) => {
    const q = date.quarter!;
    if (!acc[q]) acc[q] = 0;
    acc[q]++;
    return acc;
  }, {});

  const monthsArray: { monthYear: string; count: number }[] = [];
  const monthsMap = new Map<string, number>();

  allDates.forEach(date => {
    const m = (date as any).monthYear;
    if (!monthsMap.has(m)) {
      monthsMap.set(m, 0);
      monthsArray.push({ monthYear: m, count: 0 });
    }
    monthsMap.set(m, monthsMap.get(m)! + 1);
  });

  monthsArray.forEach(month => {
    month.count = monthsMap.get(month.monthYear)!;
  });

  return (
    <div className="absolute top-0" style={{ height: `max(${totalHeight}px, 100%)`}}>
      <div className="sticky top-0 z-40 bg-gray-50 dark:bg-[#111928]">
        {/* Quarters */}
        <div className="flex bg-white dark:bg-[#1a1f2e]">
          {Object.entries(quarters).map(([quarter, count], quarterIndex) => (
            <HeaderSection
              key={quarter}
              label={quarter}
              width={count * spacing}
              index={quarterIndex}
            />
          ))}
        </div>

        {/* Months */}
        <div className="flex bg-white dark:bg-[#1a1f2e]">
          {monthsArray.map(({ monthYear, count }) => (
            <HeaderSection
              key={monthYear}
              label={monthYear.split(' ')[0]}
              width={count * spacing}
            />
          ))}
        </div>

        {/* Days */}
        <div className="flex bg-white dark:bg-[#1a1f2e]">
          {allDates.map((dateInfo) => (
            <DayHeader
              key={dateInfo.date}
              date={dateInfo.date}
              spacing={spacing}
              viewType="quarterly"
            />
          ))}
        </div>
      </div>

      {/* Grid lines */}
      <div 
        className="absolute top-0 left-0 w-full"
        style={{ height: `max(${totalHeight}px, 100%)` }}
        {...mouseHandlers}
      >
        {allDates.map((dateInfo, index) => {
          const milestonesForDate = milestones?.filter(milestone => {
            const milestoneDate = moment(milestone.start_date).format('YYYY-MM-DD');
            return milestoneDate === moment(dateInfo.date).format('YYYY-MM-DD');
          }) || [];

          const isToday = moment(dateInfo.date).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');
          const isStart = moment(dateInfo.date).format('YYYY-MM-DD') === moment(startDate).format('YYYY-MM-DD');
          const endFlagTargetDate = getNextWorkingDay(endDate);
          const isEnd = moment(dateInfo.date).format('YYYY-MM-DD') === endFlagTargetDate;

          return (
            <div key={`quarterly-grid-${dateInfo.date}`}>
              <GridLine
                left={index * spacing}
                width={spacing}
                height="100%"
                isWeekend={dateInfo.nextDayIsWeekend}
                viewType="quarterly"
              />

              {dateInfo.nextDayIsHoliday && (
                <HolidayFlag
                  holidays={dateInfo.next_holiday_details || []}
                  date={dateInfo.date}
                  viewType="quarterly"
                  position={{
                    left: `${((index + 1) * spacing)-1}px`,
                    top: '107px'
                  }}
                  transform="translateX(-100%)"
                />
              )}

              {currentEntity === ENTITY.ROADMAP && milestonesForDate.map((milestone, milestoneIndex) => (
                <MilestoneFlag
                  key={`milestone-${milestone.id}-${milestoneIndex}`}
                  milestone={milestone}
                  position={{
                    left: `${index * spacing}px`,
                    top: '107px'
                  }}
                />
              ))}

              {isToday && (
                <CurrentDateFlag
                  position={{
                    left: `${index * spacing}px`,
                    top: '101.5px'
                  }}
                />
              )}

              {isStart && (
                <StartDateFlag
                  date={startDate}
                  position={{
                    left: `${index * spacing}px`,
                    top: '101.5px'
                  }}
                />
              )}

              {isEnd && (
                <EndDateFlag
                  originalDate={endDate}
                  position={{
                    left: `${index * spacing}px`,
                    top: '107px'
                  }}
                />
              )}

              {isMouseInTimeline && mousePosition && (
                <DateIndicatorFlag
                  date={mousePosition.date}
                  left={mousePosition.x}
                  viewType="quarterly"
                  top={topPosition}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuarterlyGridView;